
import { AnimationManager } from "../AnimationManager";
import { ClipSizeComponent } from "../components/ClipSizeComponent";
import { NoneValueSignal } from "../../signal/NoneValueSignal";

import Container = createjs.Container;
import DisplayObject = createjs.DisplayObject;
import Sprite = createjs.Sprite;
import { IGoodgameClip } from "./IGoodgameClip";

// TODO: @HTML5 - move the implementation of IGoodgameClip up on the inheritance chain
export class AbstractGoodgameClip extends Container implements IGoodgameClip {
    public isInteractive: boolean;
    /** Signal for the end of the last loop */
    public onLoopEnd: NoneValueSignal = new NoneValueSignal();

    /** animation manager for all clips. */
    protected animationManager: AnimationManager;

    /** constant that represents invinite looping of clip. */
    public static LOOPS_INFINITE: number = -1;

    /** the current frame of animation. */
    protected _currentFrame: number = 1;

    /** the current frame of animation. */
    public get currentFrame(): number {
        return this._currentFrame;
    }

    /** number of frames of the current FrameRange. */
    protected _numFrames: number;

    /** Return the amount of frames of the current FrameRange. */
    public get numFrames(): number {
        return this._numFrames;
    }

    /** playspeed of this clip. */
    protected _tagetFps: number;

    /** playspeed of this clip. */
    public set targetFps(value: number) {
        this._tagetFps = value;
        this.timePerFrame = 1000 / this._tagetFps;
        this.animationLength = this.timePerFrame * this._numFrames;
    }

    /** should-be-rendertime fo frame. */
    protected timePerFrame: number;

    /** length of animation in milliseconds. */
    protected animationLength: number;

    /** expired time of animation. */
    protected expiredAnimationTime: number = 0;

    /** @private start Frame in the Animationloop */
    protected _startFrame: number = 0;

    /** end Frame in the Animationloop */
    protected get startFrame(): number {
        return this._startFrame;
    }

    /** @private end Frame in the Animationloop */
    protected _endFrame: number;

    /** end Frame in the Animationloop */
    public get endFrame(): number {
        return this._endFrame;
    }

    /** amount of loops. Default is -1 */
    protected _loops: number = AbstractGoodgameClip.LOOPS_INFINITE;

    protected _playedLoops: number;

    /** played loops since start */
    public get playedLoops(): number {
        return this._playedLoops;
    }

    /** pause status */
    protected _paused: boolean = false;

    /** pause status */
    public get paused(): boolean {
        return this._paused;
    }

    /** toggles the pause status */
    public togglePause(): void {
        this._paused = !this._paused;
    }

    /** playOnPause status */
    protected _playOnPause: boolean = false;

    /** playOnPause status */
    public get playOnPause(): boolean {
        return this._playOnPause;
    }

    /** playOnPause status */
    public set playOnPause(value: boolean) {
        this._playOnPause = value;
    }

    /** @private running flag if clip the clip is playing */
    protected _running: boolean;

    /** running flag if clip the clip is playing */
    public get running(): boolean {
        return this._running;
    }

    protected _clipSizeComponent: ClipSizeComponent;

    public get clipSizeComponent(): ClipSizeComponent {
        return this._clipSizeComponent;
    }

    public set clipSizeComponent(clipSize: ClipSizeComponent) {
        this._clipSizeComponent = clipSize;
    }

    /**
     * start playing this clip
     */
    public play(): void {
        if (this._tagetFps < 1) {
            this.jumpToFrame(1);
            return;
        }
        this._running = true;
        this._numFrames = (this.endFrame - this.startFrame) + 1;
        this.animationLength = this.timePerFrame * this._numFrames;
        this.animationManager.register(this as IGoodgameClip);
    }

    /**
     * stop playing this clip
     */
    public stop(): void {
        this._running = false;
        this.expiredAnimationTime = 0;
        this.animationManager.unregister(this as IGoodgameClip);
    }

    /**
     * Starts playing the animation at the specified frame
     *
     * @param sFrame startFrame of the Animation
     * @param eFrame endFrame of the Animation
     * @param loops Number of the loops to play the animation default = -1
     *
     */
    public gotoAndPlay(startFrame: number, endFrame: number = -1, loops: number = -1): void {
        this.stop();
        this._loops = loops;

        if (endFrame === -1) {
            endFrame = this.numFrames;
        }

        this.setStartAndEndFrame(startFrame, endFrame);

        if (startFrame !== endFrame) {
            this.play();
        } else {
            this.stop();
        }
    }

    /**
     * Brings the playhead to the specified frame of the movie clip and stops it there.
     *
     * @param frame Number to goTo
     *
     */
    public gotoAndStop(frame: number): void {
        this.stop();
        this.setStartAndEndFrame(frame, frame);

        if (frame !== this._currentFrame) {
            this.jumpToFrame(frame);
        }
    }

    /**
     * Sets the Framerange
     *
     * @param startFrame startFrame of the Animation
     * @param endFrame endFrame of the Animation
     *
     */
    private setStartAndEndFrame(startFrame: number, endFrame: number): void {
        this._startFrame = startFrame;
        this._endFrame = endFrame;
    }

    /**
     * update current visible frame, will be called on every enterframe if this clip is registered to AnimatonManager, interface implementation
     *
     * @param deltaTime rendertime for last frame
     *
     */
    public updateFrame(deltaTime: number): void {
        if (this._paused) {
            return;
        }

        this.expiredAnimationTime += deltaTime;

        // calculate the number of current loops
        this._playedLoops = Math.floor(this.expiredAnimationTime / this.animationLength);

        // calculate next frame number to display
        let newFrame: number = Math.max(1, Math.ceil((this.expiredAnimationTime - (this.animationLength * this._playedLoops)) / this.timePerFrame) + this.startFrame - 1);

        if (this._loops !== AbstractGoodgameClip.LOOPS_INFINITE && this._playedLoops >= this._loops) {
            this.stop();
            this.onLoopEnd.signal();
        } else {
            if (newFrame !== this._currentFrame) {
                this.jumpToFrame(newFrame);
            }
        }
    }

    /**
     * @private jump to a specific frame and set current frameindex in frameCache
     *
     * @param frame frame-number to switch to
     *
     */
    protected jumpToFrame(frame: number): void {
        // for Override
    }

    /**
     * @public Interface implementation. Retuns the Bitmapclip as DisplayObject
     */
    public asDisplayObject(): DisplayObject {
        return this;
    }


    public dispose(): void {
    }



    public once(type: string, listener: Function, thisObject?: any, useCapture?: boolean, priority?: number): void {
    }
}
